import React, { useState, useEffect, useRef } from "react";

interface ImageViewerProps {
  signedUrl: string;
  alt?: string;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({ signedUrl, alt }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));

  const zoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.2, 0.5);
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 }); // Reset position at normal scale
      } else {
        // Constrain position when zooming out
        constrainPosition(newScale);
      }
      return newScale;
    });
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Function to constrain position to keep image in view
  const constrainPosition = (currentScale: number) => {
    if (!containerRef.current || !imageRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Calculate scaled image dimensions
    const scaledWidth = imageDimensions.width * currentScale;
    const scaledHeight = imageDimensions.height * currentScale;

    // Calculate maximum allowed panning distance
    const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

    setPosition((prev) => ({
      x: Math.min(Math.max(prev.x, -maxX), maxX),
      y: Math.min(Math.max(prev.y, -maxY), maxY),
    }));
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    } else if (scale > 1) {
      e.preventDefault(); // Prevent default scrolling
      setPosition((prev) => {
        const newX = prev.x - e.deltaX;
        const newY = prev.y - e.deltaY;

        if (!containerRef.current || !imageRef.current) return prev;

        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate scaled image dimensions
        const scaledWidth = imageDimensions.width * scale;
        const scaledHeight = imageDimensions.height * scale;

        // Calculate maximum allowed panning distance
        const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
        const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

        return {
          x: Math.min(Math.max(newX, -maxX), maxX),
          y: Math.min(Math.max(newY, -maxY), maxY),
        };
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const newX = e.clientX - startPos.x;
      const newY = e.clientY - startPos.y;

      if (!containerRef.current || !imageRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scaled image dimensions
      const scaledWidth = imageDimensions.width * scale;
      const scaledHeight = imageDimensions.height * scale;

      // Calculate maximum allowed panning distance
      const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
      const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

      setPosition({
        x: Math.min(Math.max(newX, -maxX), maxX),
        y: Math.min(Math.max(newY, -maxY), maxY),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle image load to get dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      });
    }
  };

  // Adjust position when scale changes
  useEffect(() => {
    constrainPosition(scale);
  }, [scale]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          zoomIn();
        } else if (e.key === "-") {
          e.preventDefault();
          zoomOut();
        } else if (e.key === "0") {
          e.preventDefault();
          resetZoom();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Add event listeners for mouse up outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  return (
    <div className="w-full h-full flex flex-col relative">
      <div className="p-2 flex gap-2 justify-end">
        {scale !== 1 && (
          <button
            onClick={() => setScale(1)}
            className="bg-gray-200 rounded p-1 hover:bg-gray-300"
            aria-label="Reset zoom"
            title="Reset zoom (Ctrl + 0)"
          >
            Reset Zoom
          </button>
        )}
        <button
          onClick={zoomOut}
          className="bg-gray-200 rounded p-1 hover:bg-gray-300"
          aria-label="Zoom out"
          title="Zoom out (Ctrl + - or scroll down)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z" />
          </svg>
        </button>
        <button
          onClick={resetZoom}
          className="bg-gray-200 rounded px-2 py-1 hover:bg-gray-300 text-xs"
          aria-label="Reset zoom"
          title="Reset zoom (Ctrl + 0)"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          onClick={zoomIn}
          className="bg-gray-200 rounded p-1 hover:bg-gray-300"
          aria-label="Zoom in"
          title="Zoom in (Ctrl + + or scroll up)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
          </svg>
        </button>
      </div>
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden flex items-center justify-center"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
      >
        <img
          ref={imageRef}
          src={signedUrl}
          alt={alt || "Document preview"}
          className="max-w-full max-h-full object-contain transition-transform duration-100"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transformOrigin: "center",
            pointerEvents: "none",
          }}
          onLoad={handleImageLoad}
        />
      </div>
    </div>
  );
};
